import PropTypes from 'prop-types';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { ProgressiveImageBase } from './progressive-image-base.component.js';
import { ErrorBoundary } from '../error-boundary/error-boundary.component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { RenderComp } from '../core/render-comp.component.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * Callback function to handle error events.
   */ onError: PropTypes.func,
    /**
   * Specifies when to trigger the image loading. Can be one of 'immediate', 'inView', or a number (delay in milliseconds).
   * Available options: immediate, inView, number
   */ trigger: PropTypes.oneOfType([
        PropTypes.oneOf([
            'immediate',
            'inView'
        ]),
        PropTypes.number
    ]),
    children: PropTypes.func
};
const propKeysToRemove = [
    'trigger',
    'intersectionOptions'
];
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProgressiveImage = renderWithRef('ProgressiveImage', propTypes, (props, _ref)=>{
    const { children, onError, placeholder = '', src: inputSrc = '', srcSet, sizes, trigger = 'inView', intersectionOptions = {
        threshold: 0
    } } = props;
    const [imageData, setImageData] = React.useState({
        delay: 0,
        src: placeholder,
        sizes: undefined,
        srcSet: undefined
    });
    const { ref, inView } = useInView(_object_spread_props(_object_spread({}, intersectionOptions), {
        triggerOnce: true
    }));
    React.useEffect(()=>{
        if (trigger === 'immediate' || trigger === 'inView' && inView) {
            setImageData({
                src: inputSrc,
                srcSet,
                sizes,
                delay: 0
            });
        } else if (typeof trigger === 'number') {
            setImageData({
                src: inputSrc,
                srcSet,
                sizes,
                delay: trigger
            });
        }
    }, [
        trigger,
        inputSrc,
        srcSet,
        sizes,
        inView
    ]);
    const classNames = new Set([
        'swan-progressive-image-wrapper'
    ]);
    return /*#__PURE__*/ React.createElement(ErrorBoundary, {
        fallback: children({
            src: inputSrc
        }, false)
    }, /*#__PURE__*/ React.createElement(RenderComp, {
        root: "div",
        propKeysToRemove: propKeysToRemove,
        forwardedRef: ref,
        props: props,
        classNames: classNames
    }, /*#__PURE__*/ React.createElement(ProgressiveImageBase, {
        src: imageData.src,
        placeholder: placeholder,
        srcSetData: {
            srcSet: imageData.srcSet || '',
            sizes: imageData.sizes || ''
        },
        delay: imageData.delay,
        onError: (evt)=>{
            if (onError) onError(evt);
        }
    }, (src, loading, srcSetData)=>children({
            src,
            srcSet: srcSetData === null || srcSetData === void 0 ? void 0 : srcSetData.srcSet,
            sizes: srcSetData === null || srcSetData === void 0 ? void 0 : srcSetData.sizes
        }, // if `immediate` => return always `loading` value
        // if `inView` and not on viewport => return `true`, no matter what `loading` says (!inView will return true and "true || anything" is true)
        // if `inView` and on viewport => return `loading` value
        // if `delay` => return `loading`
        trigger !== 'immediate' && trigger === 'inView' && !inView || loading))));
});
ProgressiveImage.defaultProps = {
    trigger: 'inView'
};

export { ProgressiveImage };
